import React, { useEffect, useRef, useState } from 'react';
import { ideas as mockIdeas } from '../data/mock';
import { api } from '../lib/api';
import { X } from 'lucide-react';

const IdeaCard = ({ item, onClick }) => (
  <button
    onClick={onClick}
    className="relative shrink-0 w-[240px] sm:w-[260px] h-[180px] sm:h-[200px] rounded-xl overflow-hidden border border-[#1f2937] group focus:outline-none focus:ring-2 focus:ring-[#ff7a1a]"
  >
    <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
    <div className="absolute bottom-0 left-0 right-0 p-3 text-left">
      <p className="text-white text-[13.5px] font-semibold leading-snug">{item.sub}</p>
    </div>
  </button>
);

const IdeasCarousel = () => {
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [active, setActive] = useState(null);
  const [ideas, setIdeas] = useState(mockIdeas);

  useEffect(() => {
    api.ideas().then((d) => { if (d?.length) setIdeas(d); }).catch(() => {});
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let raf;
    const speed = 0.5;
    const tick = () => {
      if (!paused && track) {
        track.scrollLeft += speed;
        if (track.scrollLeft >= track.scrollWidth / 2) {
          track.scrollLeft = 0;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused]);

  const list = [...ideas, ...ideas];

  return (
    <section className="bg-[#0b0b0c] pb-14 lg:pb-16" id="ideas">
      <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
        <h2 className="text-white text-3xl lg:text-4xl font-extrabold tracking-tight">Ideas you can start this week</h2>
        <div
          className="mt-6 rounded-xl border border-[#1f2937] bg-[#0f1115] p-4 overflow-hidden"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            ref={trackRef}
            className="flex gap-4 overflow-x-auto no-scrollbar scroll-smooth"
            style={{ scrollbarWidth: 'none' }}
          >
            {list.map((it, i) => (
              <IdeaCard key={i} item={it} onClick={() => setActive(it)} />
            ))}
          </div>
          <p className="mt-3 text-[#7c8597] text-sm">Hover to pause. Tap a tile for sets, reps and form cues.</p>
        </div>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setActive(null)}>
          <div className="max-w-lg w-full bg-[#0f1115] border border-[#2a2f3a] rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <img src={active.img} alt={active.title} className="w-full h-56 object-cover" />
              <button onClick={() => setActive(null)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              <h3 className="text-white font-extrabold text-xl">{active.title}</h3>
              <p className="text-[#cbb39a] text-sm mt-1">{active.sub}</p>
              <div className="mt-4">
                <p className="text-[#ff7a1a] font-bold text-sm uppercase tracking-wide">Sets • Reps</p>
                <p className="text-[#d3d8e0] mt-1">{active.sets}</p>
              </div>
              <div className="mt-4">
                <p className="text-[#ff7a1a] font-bold text-sm uppercase tracking-wide">Form cues</p>
                <p className="text-[#aab1bf] mt-1 leading-relaxed">{active.cues}</p>
              </div>
              <button onClick={() => setActive(null)} className="mt-5 px-4 py-2 rounded-md bg-[#ff7a1a] text-[#0b0b0c] font-bold text-sm hover:bg-[#ff8a33] transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default IdeasCarousel;
